<template>
  <Panel title="Related Chords">
    <p class="max-w-lg mx-auto mt-4 text-sm font-light leading-tight text-gray-600">
      The chords below are from the diatonic major scale, the secondary dominants of those diatonic chords as well as the parallel minor of the major key.
    </p>

    <div class="overflow-y-auto custom-scroll">
      <table class="w-full max-w-xl mx-auto mt-4 mb-2 font-serif table-auto">
        <thead>
          <tr>
            <th colspan="3" class="px-4 py-2 text-xs font-bold uppercase whitespace-no-wrap">{{scaleType.replace("_", " ")}} Scale</th>
            <th colspan="2" class="px-4 py-2 text-xs font-bold uppercase whitespace-no-wrap">Sec. Dominant</th>
            <th colspan="2" class="px-4 py-2 text-xs font-bold uppercase whitespace-no-wrap">Parallel {{scaleType === 'major' ? 'minor' : 'major'}}</th>
          </tr>
        </thead>

        <tbody>
          <tr :class="index % 2 ? '' : 'bg-gray-100'" v-for="index in 7" :key="index">
            <td class="px-4 py-2 text-gray-600 border" v-html="chords[scaleType][index-1][0]"/>
            <td class="px-4 py-2 font-bold border" v-html="formatChord(chords[scaleType][index-1][1])"/>
            <td class="px-4 py-2 font-bold border" v-html="formatChord(chords[scaleType][index-1][2])"/>
            <td class="px-4 py-2 text-gray-600 whitespace-no-wrap border border-l-4" v-html="chords[scaleType][index-1][3]"/>
            <td class="px-4 py-2 font-bold border" v-html="formatChord(chords[scaleType][index-1][4])"/>
            <td class="px-4 py-2 text-gray-600 border border-l-4" v-html="formatChord(chords[scaleType][index-1][5])"/>
            <td class="px-4 py-2 font-bold border" v-html="formatChord(chords[scaleType][index-1][6])"/>
          </tr>
        </tbody>
      </table>
    </div>
  </Panel>
</template>

<script>
import Panel from "./Panel.vue";
import teoria from "teoria";
const note = teoria.note;
const scale = teoria.scale;

export default {
  props: {
    rootNote: String,
    majorSevenSymbol: String,
    minorSevenSymbol: String,
    minorSymbol: String,
    diminishedSymbol: String,
    scaleType: String,
    formatChord: Function
  },
  components: { Panel },
  computed: {
    chromaticScale() {
      return scale(note(this.rootNote), "chromatic");
    },
    majorScale() {
      return scale(note(this.rootNote), "major");
    },
    minorScale() {
      return scale(note(this.rootNote), "minor");
    },
    harmonicMinorScale() {
      return scale(note(this.rootNote), "harmonicminor");
    },
    melodicMinorScale() {
      return scale(note(this.rootNote), "melodicminor");
    },
    chords() {
    return {
      major: [
        [
          "I",
          this.majorScale.notes()[0].chord(),
          this.majorScale.notes()[0].chord("maj7"),
          "—",
          "—",
          "i",
          this.minorScale.notes()[0].chord("m7")
        ],
        [
          "ii",
          this.majorScale.notes()[1].chord("m"),
          this.majorScale.notes()[1].chord("m7"),
          "V/ii (VI7)",
          this.majorScale.notes()[5].chord("7"),
          "ii˚",
          this.minorScale.notes()[1].chord("dim7")
        ],
        [
          "iii",
          this.majorScale.notes()[2].chord("m"),
          this.majorScale.notes()[2].chord("m7"),
          "V/iii (VII7)",
          this.majorScale.notes()[6].chord("7"),
          "♭III",
          this.minorScale.notes()[2].chord("maj7")
        ],
        [
          "IV",
          this.majorScale.notes()[3].chord(),
          this.majorScale.notes()[3].chord("maj7"),
          "V/IV (I7)",
          this.majorScale.notes()[0].chord("7"),
          "iv",
          this.minorScale.notes()[3].chord("m7")
        ],
        [
          "V",
          this.majorScale.notes()[4].chord(),
          this.majorScale.notes()[4].chord("7"),
          "V/V (II7)",
          this.majorScale.notes()[1].chord("7"),
          "v",
          this.minorScale.notes()[4].chord("m7")
        ],
        [
          "vi",
          this.majorScale.notes()[5].chord("m"),
          this.majorScale.notes()[5].chord("m7"),
          "V/vi (III7)",
          this.majorScale.notes()[2].chord("7"),
          "♭VI",
          this.minorScale.notes()[5].chord("maj7")
        ],
        [
          "vii°",
          this.majorScale.notes()[6].chord("dim"),
          this.majorScale.notes()[6].chord("dim7"),
          "—",
          "—",
          "♭vii",
          this.minorScale.notes()[6].chord("7")
        ],
      ],
      natural_minor: [
        [
          "i",
          this.minorScale.notes()[0].chord("m"),
          this.minorScale.notes()[0].chord("m7"),
          "—",
          "—",
          "I",
          this.majorScale.notes()[0].chord("maj7")
        ],
        [
          "ii°",
          this.minorScale.notes()[1].chord("dim"),
          this.minorScale.notes()[1].chord("dim7"),
          "—",
          "—",
          "ii",
          this.majorScale.notes()[1].chord("m7")
        ],
        [
          "III",
          this.minorScale.notes()[2].chord(),
          this.minorScale.notes()[2].chord("maj7"),
          "V/III (VII7)",
          this.minorScale.notes()[6].chord("7"),
          "iii",
          this.majorScale.notes()[2].chord("m7")
        ],
        [
          "iv",
          this.minorScale.notes()[3].chord("m"),
          this.minorScale.notes()[3].chord("m7"),
          "V/iv (I7)",
          this.minorScale.notes()[0].chord("7"),
          "IV",
          this.majorScale.notes()[3].chord("maj7")
        ],
        [
          "v",
          this.minorScale.notes()[4].chord("m"),
          this.minorScale.notes()[4].chord("m7"),
          "V/v (II7)",
          this.minorScale.notes()[1].chord("7"),
          "V",
          this.majorScale.notes()[4].chord("7")
        ],
        [
          "VI",
          this.minorScale.notes()[5].chord(),
          this.minorScale.notes()[5].chord("maj7"),
          "V/VI (III7)",
          this.minorScale.notes()[2].chord("7"),
          "vi",
          this.majorScale.notes()[5].chord("m7")
        ],
        [
          "VII",
          this.minorScale.notes()[6].chord(),
          this.minorScale.notes()[6].chord("7"),
          "V/VII (III7)",
          this.minorScale.notes()[3].chord("7"),
          "vii˚",
          this.majorScale.notes()[6].chord("dim7")
        ],
      ],
      harmonic_minor: [
        [
          "i",
          this.harmonicMinorScale.notes()[0].chord("m"),
          this.harmonicMinorScale.notes()[0].chord("mM7"),
          "—",
          "—",
          "I",
          this.majorScale.notes()[0].chord("maj7")
        ],
        [
          "iiø",
          this.harmonicMinorScale.notes()[1].chord("dim"),
          this.harmonicMinorScale.notes()[1].chord("m7b5"),
          "—",
          "—",
          "ii",
          this.majorScale.notes()[1].chord("m7")
        ],
        [
          "III",
          this.harmonicMinorScale.notes()[2].chord("aug"),
          this.harmonicMinorScale.notes()[2].chord("maj7#5"),
          "V/III (VII7)",
          this.minorScale.notes()[6].chord("7"),
          "iii",
          this.majorScale.notes()[2].chord("m7")
        ],
        [
          "iv",
          this.harmonicMinorScale.notes()[3].chord("m"),
          this.harmonicMinorScale.notes()[3].chord("m7"),
          "V/iv (I7)",
          this.harmonicMinorScale.notes()[0].chord("7"),
          "IV",
          this.majorScale.notes()[3].chord("maj7")
        ],
        [
          "V",
          this.harmonicMinorScale.notes()[4].chord(),
          this.harmonicMinorScale.notes()[4].chord("7"),
          "V/V (II7)",
          this.harmonicMinorScale.notes()[1].chord("7"),
          "V",
          this.majorScale.notes()[4].chord("7")
        ],
        [
          "VI",
          this.harmonicMinorScale.notes()[5].chord(),
          this.harmonicMinorScale.notes()[5].chord("maj7"),
          "V/VI (III7)",
          this.harmonicMinorScale.notes()[2].chord("7"),
          "vi",
          this.majorScale.notes()[5].chord("m7")
        ],
        [
          "vii˚",
          this.harmonicMinorScale.notes()[6].chord("dim"),
          this.harmonicMinorScale.notes()[6].chord("dim7"),
          "—",
          "—",
          "vii˚",
          this.majorScale.notes()[6].chord("dim7")
        ],
      ],
      melodic_minor: [
        [
          "i",
          this.melodicMinorScale.notes()[0].chord("m"),
          this.melodicMinorScale.notes()[0].chord("mM7"),
          "—",
          "—",
          "I",
          this.majorScale.notes()[0].chord("maj7")
        ],
        [
          "ii",
          this.melodicMinorScale.notes()[1].chord("m"),
          this.melodicMinorScale.notes()[1].chord("m7"),
          "—",
          "—",
          "ii",
          this.majorScale.notes()[1].chord("m7")
        ],
        [
          "III",
          this.melodicMinorScale.notes()[2].chord("aug"),
          this.melodicMinorScale.notes()[2].chord("maj7#5"),
          "V/III (VII7)",
          this.minorScale.notes()[6].chord("7"),
          "iii",
          this.majorScale.notes()[2].chord("m7")
        ],
        [
          "IV",
          this.melodicMinorScale.notes()[3].chord(),
          this.melodicMinorScale.notes()[3].chord("7"),
          "V/IV (I7)",
          this.melodicMinorScale.notes()[0].chord("7"),
          "IV",
          this.majorScale.notes()[3].chord("maj7")
        ],
        [
          "V",
          this.melodicMinorScale.notes()[4].chord(),
          this.melodicMinorScale.notes()[4].chord("7"),
          "V/V (II7)",
          this.melodicMinorScale.notes()[1].chord("7"),
          "V",
          this.majorScale.notes()[4].chord("7")
        ],
        [
          "vi",
          this.melodicMinorScale.notes()[5].chord("dim"),
          this.melodicMinorScale.notes()[5].chord("m7b5"),
          "—",
          "—",
          "vi",
          this.majorScale.notes()[5].chord("m7")
        ],
        [
          "vii˚",
          this.melodicMinorScale.notes()[6].chord("dim"),
          this.melodicMinorScale.notes()[6].chord("m7b5"),
          "—",
          "—",
          "vii˚",
          this.majorScale.notes()[6].chord("dim7")
        ],
      ],
    }
    }
  }
};
</script>
